/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/services/location.ts
 * PURPOSE: Business logic service layer implementation
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Sprint Governance - Rule 7: API Contract)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * CTO MANDATE: Production-ready React component with enterprise quality standards
 *
 * @business-context
 * - Domain: API Integration, Backend Communication, Vietnamese F&B Data Layer
 * - Impact: Data Consistency, API Performance, Error Handling
 * - Stakeholders: Development Team, Backend Team, Operations
 *
 * @technical-architecture
 * - Layer: Data Access Layer, API Services
 * - Pattern: Service Pattern, HTTP Client, Error Handling
 * - Dependencies: Axios, TypeScript, API Contracts
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/
 * - API: docs/03-integrate/01-API-Specifications/
 *  */

import { GeoLocation } from "../types/attendance";
import { WorkLocation } from "../types/auth";
import { AppError, ErrorCode } from "../types/errors";
import { GPSConfig } from "../config/enums";

export class LocationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "LocationError";
  }
}

// Development mode flag
const isDevelopment = import.meta.env.DEV;

// S94L-008: FALLBACK_LOCATION only available in development mode
// In staging/production, GPS must be real — no fake coordinates allowed
const FALLBACK_LOCATION: GeoLocation | null = isDevelopment
  ? { lat: 10.7769, lon: 106.7009, accuracy: GPSConfig.DEFAULT_ACCURACY }
  : null;  // S94L-008: null in staging/prod — forces real GPS

export const locationService = {
  /**
   * Get current location
   */
  async location_current_get__uses_gps__returns_coords(): Promise<GeoLocation> {
    // Check if we should force real GPS (for production)
    const forceRealGPS =
      !isDevelopment || localStorage.getItem("forceRealGPS") === "true";

    // S94L-008: Only use fallback in dev mode (FALLBACK_LOCATION is null in staging/prod)
    if (!forceRealGPS && !navigator.geolocation && FALLBACK_LOCATION) {
      return Promise.resolve(FALLBACK_LOCATION);
    }

    if (!navigator.geolocation) {
      throw new AppError(
        ErrorCode.LOCATION_UNAVAILABLE,
        "Geolocation is not supported by your browser",
      );
    }

    return new Promise((resolve, reject) => {
      // S94L-008: Fallback timeout only in dev mode when FALLBACK_LOCATION is available
      const timeoutId = (!forceRealGPS && FALLBACK_LOCATION)
        ? setTimeout(() => {
            resolve(FALLBACK_LOCATION);
          }, 5000)
        : null;

      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (timeoutId) clearTimeout(timeoutId);
          resolve({
            lat: position.coords.latitude,
            lon: position.coords.longitude,
            accuracy: position.coords.accuracy,
          });
        },
        (error) => {
          if (timeoutId) clearTimeout(timeoutId);

          // S94L-008: Only use fallback in dev mode when available
          if (isDevelopment && FALLBACK_LOCATION && error.code === error.PERMISSION_DENIED) {
            resolve(FALLBACK_LOCATION);
            return;
          }
          let errorCode: ErrorCode;
          let message: string;

          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorCode = ErrorCode.LOCATION_PERMISSION_DENIED;
              message = "Location permission denied";
              break;
            case error.POSITION_UNAVAILABLE:
              errorCode = ErrorCode.LOCATION_UNAVAILABLE;
              message = "Location information is unavailable";
              break;
            case error.TIMEOUT:
              errorCode = ErrorCode.LOCATION_TIMEOUT;
              message = "Location request timed out";
              break;
            default:
              errorCode = ErrorCode.LOCATION_UNAVAILABLE;
              message = "Unable to get location";
          }

          reject(new AppError(errorCode, message));
        },
        {
          enableHighAccuracy: GPSConfig.HIGH_ACCURACY,
          timeout: GPSConfig.TIMEOUT,
          maximumAge: 0,
        },
      );
    });
  },

  /**
   * Watch location changes
   */
  watchLocation(
    onSuccess: (location: GeoLocation) => void,
    onError: (error: AppError) => void,
  ): number {
    if (!navigator.geolocation) {
      onError(
        new AppError(
          ErrorCode.LOCATION_UNAVAILABLE,
          "Geolocation is not supported",
        ),
      );
      return -1;
    }

    return navigator.geolocation.watchPosition(
      (position) => {
        onSuccess({
          lat: position.coords.latitude,
          lon: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });
      },
      (error) => {
        let errorCode: ErrorCode;
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorCode = ErrorCode.LOCATION_PERMISSION_DENIED;
            break;
          case error.POSITION_UNAVAILABLE:
            errorCode = ErrorCode.LOCATION_UNAVAILABLE;
            break;
          case error.TIMEOUT:
            errorCode = ErrorCode.LOCATION_TIMEOUT;
            break;
          default:
            errorCode = ErrorCode.LOCATION_UNAVAILABLE;
        }
        onError(new AppError(errorCode, error.message));
      },
      {
        enableHighAccuracy: GPSConfig.HIGH_ACCURACY,
        timeout: GPSConfig.TIMEOUT,
        maximumAge: 0,
      },
    );
  },

  /**
   * Clear location watch
   */
  clearWatch(watchId: number): void {
    if (navigator.geolocation && watchId !== -1) {
      navigator.geolocation.clearWatch(watchId);
    }
  },

  /**
   * Calculate distance between two points using Haversine formula
   */
  calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number,
  ): number {
    const R = 6371000; // Earth's radius in meters
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lon2 - lon1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  },

  /**
   * Validate if current location is within allowed radius of workplace
   */
  location_validate__checks_bounds__returns_status(
    currentLocation: GeoLocation,
    workLocation: WorkLocation,
  ): { isValid: boolean; distance: number } {
    const distance = this.calculateDistance(
      currentLocation.lat,
      currentLocation.lon,
      workLocation.geo_lat,
      workLocation.geo_lon,
    );

    const isValid = distance <= workLocation.allowed_radius;

    return { isValid, distance };
  },

  /**
   * Check if GPS accuracy meets requirements
   * @param accuracy - GPS accuracy in meters
   * @param threshold - Maximum allowed accuracy (default from GPSConfig)
   * @returns validation result with details
   */
  validateAccuracy(
    accuracy: number | undefined,
    threshold: number = GPSConfig.DEFAULT_ACCURACY
  ): {
    isValid: boolean;
    accuracy: number;
    threshold: number;
    message?: string;
  } {
    const actualAccuracy = accuracy || 999;
    const isValid = actualAccuracy <= threshold;
    
    return {
      isValid,
      accuracy: actualAccuracy,
      threshold,
      message: !isValid 
        ? `GPS accuracy (${Math.round(actualAccuracy)}m) exceeds threshold (${threshold}m)`
        : undefined
    };
  },

  /**
   * Get accuracy threshold from environment or default
   * Uses GPSConfig for centralized configuration
   */
  getAccuracyThreshold(): number {
    return GPSConfig.DEFAULT_ACCURACY;
  },

  /**
   * Check if admin override is allowed
   */
  isAdminOverrideAllowed(userRole?: string): boolean {
  const role = (userRole || '').toString().toLowerCase();
  return role === 'admin' || role === 'manager';
  },
};
